import { Injectable, Inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { AppState } from "../models/app.state";
import { ENV_VARS } from "../environment-variables/environment-variables.module";
import { HttpClient } from "@angular/common/http";
import { UserState } from "../store/reducers/user.reducer";
import { ConfigState } from "../store/reducers/config.reducer";
import { Observable } from "rxjs/Observable";
import { HttpParams } from "@angular/common/http";

export interface ILogEvent {
  id: number;
  organization_uuid: string;
  organization_name: string;
  event_src_system: string;
  event_src_location: string;
  event_category: LogEventCategory;
  event_id: LogEventId;
  title: string;
  description: string;
  patient_fhir_id: string;
  patient_full_name: string;
  parent_task_uuid: string;
  qnr_response_uuid: string;
  acting_user_fhir_id: string;
  acting_user_login_id: string;
  acting_user_full_name: string;
  action_user_system_roles: string;
  additional_info?: { [key: string]: any };
  event_timestamp: string;
  event_updated: string;
}

export interface ILogEventSearchInput {
  patient_fhir_id?: string;
  acting_user_login_id?: string;
  start_from: string;
  result_size: string;
}

export interface ILogEventSearchGetParams extends ILogEventSearchInput {
  organization_uuid: string;
}

export interface ILogEventInput {
  event_id: LogEventId;
  event_src_location: string;
  parent_task_uuid?: string;
  qnr_response_uuid?: string;
  patient_fhir_id?: string;
  patient_full_name?: string;
  additional_info?: { [key: string]: any };
}

export interface ILogEventPostPayload extends ILogEventInput {
  organization_uuid: string;
  organization_name: string;
  event_src_system: "HCP_APP" | "SERVER";
  event_category: LogEventCategory;
  title: string;
  description: string;
}

export enum LogEventCategory {
  AUTHENTICATION = "AUTHENTICATION",
  PATIENT_MANAGEMENT = "PATIENT_MANAGEMENT",
  ADMINISTRATION = "ADMINISTRATION",
  NAVIGATION = "NAVIGATION",
  CONSULTATION = "CONSULTATION"
}

export enum LogEventId {
  // LOGIN / LOGOUT
  LOGIN = "LOGIN",
  LOGOUT = "LOGOUT",
  // PATIENT MANAGEMENT
  CREATE_PATIENT = "CREATE_PATIENT",
  DEACTIVATE_PATIENT = "DEACTIVATE_PATIENT",
  CREATE_PATIENT_TASK = "CREATE_PATIENT_TASK",
  MODIFY_PATIENT_TASK = "MODIFY_PATIENT_TASK",
  CANCEL_PATIENT_TASK = "CANCEL_PATIENT_TASK",
  // HCP MANAGEMENT
  CREATE_HCP = "CREATE_HCP",
  MODIFY = "CREATE_HCP",
  DEACTIVATE_HCP = "DEACTIVATE_HCP",
  // NAVIGATION EVENTS
  OPEN_HOME_PAGE = "OPEN_HOME_PAGE",
  OPEN_PATIENT_PAGE = "OPEN_PATIENT_PAGE",
  OPEN_QUESTIONNAIRE_RESPONSE = "OPEN_QUESTIONNAIRE_RESPONSE",
  OPEN_PATIENT_LISTING = "OPEN_PATIENT_LISTING",
  OPEN_STAFF_LISTING = "OPEN_STAFF_LISTING",
  OPEN_CURRENT_CONSULTATIONS = "OPEN_CURRENT_CONSULTATIONS",
  // CONSULTATION EVENTS
  COMPLETE_PATIENT_CONSULTATION = "COMPLETE_PATIENT_CONSULTATION",
  OPEN_SUPPORT_ACTION_MODAL = "OPEN_SUPPORT_ACTION_MODAL",
}

export const LogEventMap: {
  [key in LogEventId]: {
    title: string;
    description: string;
    event_category: LogEventCategory;
  };
} = {
  // LOGIN / LOGOUT
  [LogEventId.LOGIN]: {
    title: "Login",
    description: "HCP user login",
    event_category: LogEventCategory.AUTHENTICATION
  },
  [LogEventId.LOGOUT]: {
    title: "Logout",
    description: "HCP user log out",
    event_category: LogEventCategory.AUTHENTICATION
  },
  // PATIENT MANAGEMENT
  [LogEventId.CREATE_PATIENT]: {
    title: "Create patient",
    description: "HCP user created a patient",
    event_category: LogEventCategory.PATIENT_MANAGEMENT
  },
  [LogEventId.DEACTIVATE_PATIENT]: {
    title: "Deactivated patient",
    description: "HCP user deactivated a patient",
    event_category: LogEventCategory.PATIENT_MANAGEMENT
  },
  [LogEventId.CREATE_PATIENT_TASK]: {
    title: "Create PRO task",
    description: "HCP created a PRO task for patient",
    event_category: LogEventCategory.PATIENT_MANAGEMENT
  },
  [LogEventId.MODIFY_PATIENT_TASK]: {
    title: "Modify PRO Task",
    description: "HCP modified PRO task for patient",
    event_category: LogEventCategory.PATIENT_MANAGEMENT
  },
  [LogEventId.CANCEL_PATIENT_TASK]: {
    title: "Cancel PRO task",
    description: "HCP user cancelled PRO task",
    event_category: LogEventCategory.PATIENT_MANAGEMENT
  },
  // HCP MANAGEMENT
  [LogEventId.CREATE_HCP]: {
    title: "Created HCP user",
    description: "Created HCP user",
    event_category: LogEventCategory.ADMINISTRATION
  },
  [LogEventId.DEACTIVATE_HCP]: {
    title: "Deactivated HCP user",
    description: "HCP user login",
    event_category: LogEventCategory.ADMINISTRATION
  },
  [LogEventId.MODIFY]: {
    title: "Modified HCP user",
    description: "Modified HCP user",
    event_category: LogEventCategory.ADMINISTRATION
  },
  [LogEventId.OPEN_HOME_PAGE]: {
    title: "Open home page",
    description: "HCP user opened their own home page",
    event_category: LogEventCategory.NAVIGATION
  },
  // NAVIGATION EVENTS
  [LogEventId.OPEN_PATIENT_PAGE]: {
    title: "Open patient page",
    description: "HCP user opened patient page",
    event_category: LogEventCategory.NAVIGATION
  },
  [LogEventId.OPEN_QUESTIONNAIRE_RESPONSE]: {
    title: "Open questionnaire response",
    description: "HCP user opened questionnaire response",
    event_category: LogEventCategory.NAVIGATION
  },
  [LogEventId.OPEN_PATIENT_LISTING]: {
    title: "Open patient listing",
    description: "HCP user opened listing for all patients in organization",
    event_category: LogEventCategory.NAVIGATION
  },
  [LogEventId.OPEN_STAFF_LISTING]: {
    title: "Open staff listing",
    description: "HCP user opened listing of all HCP users in organization",
    event_category: LogEventCategory.NAVIGATION
  },
  [LogEventId.OPEN_CURRENT_CONSULTATIONS]: {
    title: "Open current consultations",
    description:
      "HCP user opened listing of current consultations in organization",
    event_category: LogEventCategory.NAVIGATION
  },
  // CONSULTATION EVENTS
  [LogEventId.COMPLETE_PATIENT_CONSULTATION]: {
    title: "Completed patient consultatino",
    description: "HCP user completed patient consultation",
    event_category: LogEventCategory.CONSULTATION
  },
  // CONSULTATION EVENTS
  [LogEventId.OPEN_SUPPORT_ACTION_MODAL]: {
    title: "Opened support actions modal consultation",
    description: "HCP user opened support actions modal in response dashboard",
    event_category: LogEventCategory.CONSULTATION
  }
};

@Injectable()
export class EventLoggingService {
  logEndpoint: string;
  userState: UserState;
  configState: ConfigState;
  constructor(
    public http: HttpClient,
    @Inject(ENV_VARS) public envVars,
    private store: Store<AppState>
  ) {
    this.logEndpoint = envVars.API_URL + "/@event-logging";
    this.store
      .select(state => state.user)
      .subscribe(userState => (this.userState = userState));
    this.store
      .select(state => state.config)
      .subscribe(configState => (this.configState = configState));
  }

  saveEvent(input: ILogEventInput): void {
    const payload: ILogEventPostPayload = {
      ...input,
      ...LogEventMap[input.event_id],
      organization_uuid: this.userState.organizationUuid,
      organization_name:
        this.configState.orgResource !== null
          ? this.configState.orgResource.name
          : null,
      event_src_system: "HCP_APP"
    };
    if (this.envVars.EVENT_LOGGING !== "YES") {
      console.info("event logging is disabled, but payload was: ", payload);
      return;
    }
    this.http.post(this.logEndpoint, { event: payload }).subscribe();
  }

  getEvents(input: ILogEventSearchInput): Observable<ILogEvent[]> {
    const params = {
      ...input,
      organization_uuid: this.userState.organizationUuid
    };
    Object.keys(params).forEach(key =>
      params[key] === undefined ? delete params[key] : {}
    );
    const httpParams = new HttpParams({ fromObject: params });
    return this.http.get<ILogEvent[]>(this.logEndpoint, { params: params });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/event-logging.service.ts